import fontkit from "@pdf-lib/fontkit";
import { PDFDocument, PDFFont, PDFPage, rgb } from "pdf-lib";

const PDF_FILENAME = "mellket_1_ovsz_recipiens_bejelento.pdf";
const PDF_TEMPLATE_PATH = `/${PDF_FILENAME}`;
const FONT_SIZE = 6;
const TEXT_COLOR = rgb(0, 0, 1);

export interface PDFData {
	transplantCenter: string;
	registrationDate: string;
	patientEtId: string;
	dialysisCentreCode: string;
	dialysisCentreName: string;
	isFirstRegistration: boolean;
	isReRegistration: boolean;

	firstName: string;
	lastName: string;
	initials: string;
	dateOfBirth: string;
	sex: string;
	citizenship: string;
	address: string;
	socialSecurityNumber: string;
	phoneNumber1: string;
	phoneNumber2: string;
	bloodGroupAb0: string;
	bloodGroupRh: string;
	weight: string;
	height: string;
	transfusionBeforeRegistration: string;
	transfusionAfterRegistration: string;
	pregnancies: string;
	children: string;

	viralSelogoyDate: string;
	antiHiv: string;
	hbsAg: string;
	antiHbs: string;
	antiHbc: string;
	antiHcv: string;
	antiCmvIgg: string;
	antiCmvIgm: string;
	toxoplasmaIgg: string;
	treponemaPallidum: string;
	hbImm: string;
	antiEbv: string;

	transplantable: boolean;
	notTransplantable: boolean;
	combinedTransplant: string;
	liveDonor: string;
	previousKidneyTransplantationDate1: string;
	previousKidneyTransplantationDate2: string;
	previousKidneyTransplantationDate3: string;
	edta: string;
	renalReplacementTherapyDate: string;
	renalReplacementTherapyType: string;
	causeOfGraftloss: string;
	graftlossDate: string;
}

export async function generatePDF(data: PDFData): Promise<void> {
	const res = await fetch(PDF_TEMPLATE_PATH);
	const buf = await res.arrayBuffer();
	const doc = await PDFDocument.load(buf);
	const page = doc.getPage(0);

	doc.registerFontkit(fontkit);
	const fontResponse = await fetch("/Merriweather-Regular.ttf");
	const fontData = await fontResponse.arrayBuffer();
	const font = await doc.embedFont(fontData);

	drawText(page, font, 162, 130.5, data.transplantCenter);
	drawText(page, font, 316, 136.5, data.registrationDate);
	drawText(page, font, 270, 157, data.patientEtId);
	drawText(page, font, 171, 192.5, data.dialysisCentreCode);
	drawLongText(page, font, 329, 192.5, 198, 96, 17, data.dialysisCentreName);
	if (data.isFirstRegistration) {
		drawX(page, 445, 130);
	}
	if (data.isReRegistration) {
		drawX(page, 445, 168.5);
	}

	drawText(page, font, 227, 229.5, data.firstName);
	drawText(page, font, 50, 229.5, data.lastName);
	drawText(page, font, 362, 229.5, data.initials);
	drawText(page, font, 426, 229.5, data.dateOfBirth);
	drawText(page, font, 526, 219.5, data.sex);
	drawText(page, font, 50, 252, data.citizenship);
	drawText(page, font, 154, 252, data.address);
	drawText(page, font, 471, 252, data.socialSecurityNumber);
	drawText(page, font, 88, 264, data.phoneNumber1);
	drawText(page, font, 88, 276, data.phoneNumber2);
	drawText(page, font, 227, 275.5, data.bloodGroupAb0);
	drawText(page, font, 362, 275.5, data.bloodGroupRh);
	drawText(page, font, 435, 275.5, data.weight);
	drawText(page, font, 508, 275.5, data.height);
	drawText(page, font, 174, 290, data.transfusionBeforeRegistration);
	drawText(page, font, 196, 302, data.transfusionAfterRegistration);
	drawText(page, font, 227, 303, data.pregnancies);
	drawText(page, font, 411, 303, data.children);

	drawText(page, font, 115, 338.5, data.viralSelogoyDate);
	drawRightText(page, font, 226, 327, data.antiHiv);
	drawRightText(page, font, 283, 327, data.hbsAg);
	drawRightText(page, font, 283, 341, data.antiHbs);
	drawRightText(page, font, 347, 327, data.antiHbc);
	drawRightText(page, font, 347, 341, data.antiHcv);
	drawRightText(page, font, 418, 327, data.antiCmvIgg);
	drawRightText(page, font, 418, 341, data.antiCmvIgm);
	drawRightText(page, font, 495, 327, data.toxoplasmaIgg);
	drawRightText(page, font, 495, 341, data.treponemaPallidum);
	drawRightText(page, font, 553, 327, data.hbImm);
	drawRightText(page, font, 553, 341, data.antiEbv);

	if (data.transplantable) {
		drawX(page, 114.5, 361.25);
	}
	if (data.notTransplantable) {
		drawX(page, 214.25, 361.25);
	}
	drawText(page, font, 106, 410, data.combinedTransplant);
	drawText(page, font, 281, 410, data.liveDonor);
	drawText(page, font, 454, 384, data.previousKidneyTransplantationDate1);
	drawText(page, font, 454, 398, data.previousKidneyTransplantationDate2);
	drawText(page, font, 454, 411, data.previousKidneyTransplantationDate3);
	drawText(page, font, 268, 430, data.edta);
	drawText(page, font, 50, 456.5, data.renalReplacementTherapyDate);
	drawText(page, font, 236, 447, data.renalReplacementTherapyType);
	drawText(page, font, 429, 430, data.causeOfGraftloss);
	drawText(page, font, 445, 452, data.graftlossDate);

	const link = document.createElement("a");
	link.download = PDF_FILENAME;
	link.href = await doc.saveAsBase64({ dataUri: true });

	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}

function drawX(page: PDFPage, x: number, y: number): void {
	const { height } = page.getSize();
	page.drawLine({
		start: { x: x, y: height - y },
		end: { x: x + 6, y: height - (y + 6) },
		color: TEXT_COLOR
	});
	page.drawLine({
		start: { x: x + 6, y: height - y },
		end: { x: x, y: height - (y + 6) },
		color: TEXT_COLOR
	});
}

function drawText(
	page: PDFPage,
	font: PDFFont,
	x: number,
	y: number,
	text: string
): void {
	const { height } = page.getSize();
	page.drawText(removeUnknownCharacters(font, text), {
		font,
		size: FONT_SIZE,
		color: TEXT_COLOR,
		x: x + 5,
		y: height - y
	});
}

function drawLongText(
	page: PDFPage,
	font: PDFFont,
	x: number,
	singleY: number,
	multiY: number,
	w: number,
	h: number,
	text: string
): void {
	const { height } = page.getSize();

	const cleanText = removeUnknownCharacters(font, text);

	const lines = [];
	let line = "";
	for (const word of cleanText.trim().split(/\s+/)) {
		const nextLine = line.length === 0 ? word : `${line} ${word}`;
		const lineWidth = font.widthOfTextAtSize(nextLine, FONT_SIZE);
		if (lineWidth > w - 10) {
			lines.push(line);
			line = word;
		} else {
			line = nextLine;
		}
	}
	if (line.length !== 0) {
		lines.push(line);
	}

	if (lines.length === 1) {
		page.drawText(lines[0], {
			font,
			size: FONT_SIZE,
			color: TEXT_COLOR,
			x: x + 5,
			y: height - singleY
		});
	} else {
		for (let i = 0, l = lines.length; i < l; i++) {
			const line = lines[i];
			page.drawText(line, {
				font,
				size: FONT_SIZE,
				color: TEXT_COLOR,
				x: x + 5,
				y: height - multiY + (l - 1 - i) * (h / lines.length) + 1.5
			});
		}
	}
}

function drawRightText(
	page: PDFPage,
	font: PDFFont,
	x: number,
	y: number,
	text: string
): void {
	const { height } = page.getSize();
	const cleanText = removeUnknownCharacters(font, text);
	const width = font.widthOfTextAtSize(cleanText, FONT_SIZE);
	page.drawText(cleanText, {
		font,
		size: FONT_SIZE,
		color: TEXT_COLOR,
		x: x - width - 5,
		y: height - y
	});
}

function removeUnknownCharacters(font: PDFFont, s: string): string {
	const chars = font.getCharacterSet();
	let out = "";
	for (let i = 0; i < s.length; i++) {
		if (chars.includes(s.charCodeAt(i))) {
			out += s.charAt(i);
		} else {
			out += "?";
		}
	}
	return out;
}
